import React from 'react';
import styles from './style.module.scss';
import PricingCard from './components/shared/PricingCard';
import RateItem from './components/shared/RateItem';
import { RegularCardIcons } from './constants/icons';
import AmexIcon24 from '../../../../../../../../../../images/v2/amex24x24.svg';

const AMEX_TIERS = [
  {
    id: 'one',
    title: 'Amex Tier 1',
    rates: [
      { label: 'Rate', id: 32 },
      { label: 'Txn fee', id: 32 }
    ]
  },
  {
    id: 'two', title: 'Amex Tier 2',
    rates: [
      { label: 'Rate', id: 33 },
      { label: 'Txn fee', id: 33 }
    ]
  },
  {
    id: 'three', title: 'Amex Tier 3',
    rates: [
      { label: 'Rate', id: 34 },
      { label: 'Txn fee', id: 34 }
    ]
  },
  {
    id: 'four', title: 'Amex Tier 4',
    rates: [
      { label: 'Rate', id: 49 },
      { label: 'Txn fee', id: 49 }
    ]
  }
];

const getPricingDataById = (id, values) => {
  let pricing = values.find((price) => price.id === id);
  return pricing ? pricing.pivot : { dollars: 0, percent: 0 };
};

const AmexFlatRateCard = ({ values, formatCurrency, formatPercentage }) => {
  const amexRates = [
    { label: 'Flat Rate', value: formatPercentage(getPricingDataById(35, values).percent) },
    { label: 'Txn Fee', value: formatCurrency(getPricingDataById(35, values).dollars) },
    null,
    null
  ];

  return (
    <div className={styles.amexCard}>
      <div className={styles.amexHeader}>
        <div className={styles.cardTitle}>Amex Flat Rate</div>
      </div>
      <div className={styles.ratesList}>
        {amexRates.map((rate, index) => (
          rate ? (
            <RateItem key={index} label={rate.label} value={rate.value} />
          ) : (
            <div key={index} className={styles.emptyRateItem} />
          )
        ))}
      </div>
    </div>
  );
};

const AmexTieredRateCard = ({ values, formatCurrency, formatPercentage }) => {

  return (
    <div className={styles.amexTieredCards}>
      {AMEX_TIERS.map((tier, index) => (
        <div key={index} className={styles.amexCard}>
          <div className={styles.amexHeader}>
            <div className={styles.cardTitle}>{tier.title}</div>
          </div>
          <div className={styles.ratesList}>
            {tier.rates.map((rate, rateIndex) => (
              <RateItem key={rateIndex} label={rate.label} value={formatPercentage(getPricingDataById(rate.id, values).percent)} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

const AmexICPlusCard = ({ values, formatCurrency, formatPercentage }) => {
  const rates = [
    { label: 'IC+ Rate', value: formatPercentage(getPricingDataById(35, values).percent) },
    { label: 'Txn Fee', value: formatCurrency(getPricingDataById(35, values).dollars) }
  ];

  return (
    <div className={styles.amexCard}>
      <div className={styles.amexHeader}>
        <div className={styles.cardTitle}>Amex Interchange Rate</div>
      </div>
      <div className={styles.ratesList}>
        {rates.map((rate, index) => (
          <RateItem key={index} label={rate.label} value={rate.value} />
        ))}
      </div>
    </div>
  );
};

const AmexSection = ({ applicationData, pricingModel, pricingData, formatCurrency, formatPercentage }) => {
  const renderAmexContent = () => {
    switch (pricingModel) {
      case 'flat_rate':
        return <AmexFlatRateCard values={pricingData} formatCurrency={formatCurrency} formatPercentage={formatPercentage} />;
      case 'tiered':
        return <AmexTieredRateCard values={pricingData} formatCurrency={formatCurrency} formatPercentage={formatPercentage} />;
      case 'ic_plus':
        return <AmexICPlusCard values={pricingData} formatCurrency={formatCurrency} formatPercentage={formatPercentage} />;
      default:
        return null;
    }
  };

  return (
    <div className={styles.amexSection}>
      <div className={styles.sectionHeader}>
        <div className={styles.sectionTitle}>AMEX STRUCTURE</div>
        <div className={styles.sectionLine} />
        <div className={styles.sectionIcon}>
          <div className={`${styles.cardIcon} ${styles.amex}`}>
            <img src={AmexIcon24} alt="AMEX" />
          </div>
        </div>
      </div>
      <div className={styles.amexDetails}>
        <RateItem
          label="Preferred AMEX Structure"
          value={
            pricingModel === 'tiered'
              ? 'Tiered Rate'
              : pricingModel === 'ic_plus'
                ? 'IC+'
                : 'Flat Rate'
          }
        />
      </div>
      {renderAmexContent()}
    </div>
  );
};

const TieredRate = ({ values, applicationData, formatCurrency, formatPercentage }) => {
  const qualifiedRates = [
    { label: 'Qual rate', value: formatPercentage(getPricingDataById(1, values).percent) },
    { label: 'Txn rate', value: formatCurrency(getPricingDataById(1, values).dollars) },
    { label: 'Debit Rate', value: formatPercentage(getPricingDataById(39, values).percent) },
    { label: 'Debit Per Txn', value: formatCurrency(getPricingDataById(39, values).dollars) }
  ];

  const midQualifiedRates = [
    { label: 'Qual rate', value: formatPercentage(getPricingDataById(3, values).percent) },
    { label: 'Txn rate', value: formatCurrency(getPricingDataById(3, values).dollars) },
    { label: 'Debit Rate', value: formatPercentage(getPricingDataById(40, values).percent) },
    { label: 'Debit Per Txn', value: formatCurrency(getPricingDataById(40, values).dollars) }
  ];

  const nonQualifiedRates = [
    { label: 'Qual rate', value: formatPercentage(getPricingDataById(4, values).percent) },
    { label: 'Txn rate', value: formatCurrency(getPricingDataById(4, values).dollars) },
    { label: 'Debit Rate', value: formatPercentage(getPricingDataById(41, values).percent) },
    { label: 'Debit Per Txn', value: formatCurrency(getPricingDataById(41, values).dollars) }
  ];

  return (
    <div className={styles.tieredRate}>
      <div className={styles.rateCards}>
        <PricingCard
          title="Qualified"
          icons={<RegularCardIcons />}
        >
          <div className={styles.ratesList}>
            {qualifiedRates.map((rate, index) => (
              <RateItem key={index} label={rate.label} value={rate.value} />
            ))}
          </div>
        </PricingCard>

        <PricingCard
          title="Mid-Qualified"
          icons={<RegularCardIcons />}
        >
          <div className={styles.ratesList}>
            {midQualifiedRates.map((rate, index) => (
              <RateItem key={index} label={rate.label} value={rate.value} />
            ))}
          </div>
        </PricingCard>

        <PricingCard
          title="Non-Qualified"
          icons={<RegularCardIcons />}
        >
          <div className={styles.ratesList}>
            {nonQualifiedRates.map((rate, index) => (
              <RateItem key={index} label={rate.label} value={rate.value} />
            ))}
          </div>
        </PricingCard>

        {applicationData.pricing_model && (
          <AmexSection applicationData={applicationData} pricingModel={applicationData.pricing_model} pricingData={applicationData.prices} formatCurrency={formatCurrency} formatPercentage={formatPercentage} />
        )}
      </div>
    </div>
  );
};

export default TieredRate; 